(() => {
  const markerPath = 'M22.6746 0C10.2174 0 0 8.79169 0 21.5118C0 31.2116 4.33864 38.333 9.26606 42.998C11.7232 45.3243 14.3387 47.0534 16.6674 48.2077C18.9384 49.3333 21.1148 50 22.6746 50C24.2345 50 26.4108 49.3333 28.6818 48.2077C31.0105 47.0534 33.626 45.3243 36.0832 42.998C41.0106 38.333 45.3492 31.2116 45.3492 21.5118C45.3492 8.79169 35.1318 0 22.6746 0ZM29.6514 22.6746C29.6514 26.5278 26.5278 29.6514 22.6746 29.6514C18.8214 29.6514 15.6978 26.5278 15.6978 22.6746C15.6978 18.8214 18.8214 15.6978 22.6746 15.6978C26.5278 15.6978 29.6514 18.8214 29.6514 22.6746Z';

  class GMap extends HTMLElement {
    constructor() {
      super();

      if (!this.hasAttribute('data-api-key')) {
        return;
      }

      if (!this.hasAttribute('data-map-address') && !(this.hasAttribute('data-latitude') && this.hasAttribute('data-longitude'))) {
        return;
      }

      this.prepMapApi = this.prepMapApi.bind(this);
      this.initMap = this.initMap.bind(this);

      if (typeof Motion !== 'undefined' && Motion && typeof Motion.inView === 'function') {
        Motion.inView(this, this.prepMapApi, { margin: '600px 0px 600px 0px' });
      } else {
        this.prepMapApi();
      }

      window.gm_authFailure = () => {
        if (window.Shopify && Shopify.designMode) {
          window.mapError(theme.strings.authError);
        }
      };

      window.mapError = (error, element) => {
        const container = element ? element.closest('.shopify-section') : document;
        if (!container) {
          return;
        }

        container.querySelectorAll('.gmap--error').forEach((message) => message.remove());

        container.querySelectorAll('g-map').forEach((map) => {
          const message = document.createElement('div');
          message.classList.add('rte', 'alert', 'alert--error', 'gmap--error');
          message.innerHTML = error;

          const wrapper = map.closest('.with-map');
          if (wrapper) {
            wrapper.prepend(message);
          }
        });
      };

      window.gmNoop = () => {};
    }

    prepMapApi() {
      if (this._mapReady) {
        return this._mapReady;
      }

      this._mapReady = this.loadScript()
        .then(() => this.initMap())
        .then(() => {
          setTimeout(() => {
            const container = this.closest('.banner__map');
            if (container && container.previousElementSibling) {
              container.previousElementSibling.classList.remove('opacity-0');
            }
          }, 1000);
        })
        .catch(() => {});

      return this._mapReady;
    }

    loadScript() {
      return new Promise((resolve, reject) => {
        if (window.google && window.google.maps) {
          resolve();
          return;
        }

        const existingScript = document.querySelector('script[data-google-maps]');
        if (existingScript) {
          existingScript.addEventListener('load', resolve, { once: true });
          existingScript.addEventListener('error', reject, { once: true });
          return;
        }

        const script = document.createElement('script');
        script.dataset.googleMaps = 'true';
        script.async = true;
        script.src = "https://maps.googleapis.com/maps/api/js?key=" + this.getAttribute('data-api-key') + "&callback=gmNoop";
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });
    }

    initMap() {
      if (!(window.google && window.google.maps)) {
        return;
      }

      const latAttr = parseFloat(this.getAttribute('data-latitude'));
      const lngAttr = parseFloat(this.getAttribute('data-longitude'));
      const hasCoordinates = !isNaN(latAttr) && !isNaN(lngAttr);

      if (hasCoordinates) {
        this.renderMap(new google.maps.LatLng(latAttr, lngAttr));
        return;
      }

      const address = this.getAttribute('data-map-address');
      if (!address) {
        return;
      }

      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: address }, (results, status) => {
        if (status !== google.maps.GeocoderStatus.OK || !results[0]) {
          if (window.Shopify && Shopify.designMode) {
            let errorMessage;

            switch (status) {
              case 'ZERO_RESULTS':
                errorMessage = theme.strings.addressNoResults;
                break;
              case 'OVER_QUERY_LIMIT':
                errorMessage = theme.strings.addressQueryLimit;
                break;
              case 'REQUEST_DENIED':
                errorMessage = theme.strings.authError;
                break;
              default:
                errorMessage = theme.strings.addressError;
                break;
            }

            window.mapError(errorMessage, this);
          }
          return;
        }

        this.renderMap(results[0].geometry.location);
      });
    }

    renderMap(location) {
      const zoom = parseInt(this.getAttribute('data-zoom'), 10);
      const markerColor = this.getAttribute('data-marker-color') || '#000000';
      const mapOptions = {
        zoom: isNaN(zoom) ? 14 : zoom,
        center: location,
        draggable: true,
        clickableIcons: false,
        scrollwheel: false,
        disableDoubleClickZoom: true,
        disableDefaultUI: true
      };

      if (this._map) {
        this._map.setOptions(mapOptions);
        this._map.setCenter(location);
      } else {
        this._map = new google.maps.Map(this, mapOptions);
      }

      if (this._marker) {
        this._marker.setPosition(location);
      } else {
        this._marker = new google.maps.Marker({
          map: this._map,
          position: location,
          icon: {
            path: markerPath,
            fillColor: markerColor,
            fillOpacity: 1,
            anchor: new google.maps.Point(15, 55),
            strokeWeight: 0,
            scale: 0.7
          }
        });
      }

      if (!this._styledMapApplied) {
        const styleNode = this.parentNode ? this.parentNode.querySelector('[data-gmap-style]') : null;
        if (styleNode) {
          try {
            const styleDefinition = JSON.parse(styleNode.innerHTML);
            const styledMapType = new google.maps.StyledMapType(styleDefinition);
            this._map.mapTypes.set('styled_map', styledMapType);
            this._map.setMapTypeId('styled_map');
            this._styledMapApplied = true;
          } catch (error) {
            console.warn('Failed to parse Google Maps style JSON.', error);
          }
        }
      }
    }
  }

  class GMapLocations extends HTMLUListElement {
    constructor() {
      super();

      this.selectedIndex = this.selectedIndex;
      this.buttons.forEach((button, index) => {
        button.addEventListener('click', () => {
          this.selectedIndex = index;
        });
      });

      if (window.Shopify && Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => {
          const selectedIndex = this.buttons.indexOf(event.target);
          if (selectedIndex > -1) {
            this.selectedIndex = selectedIndex;
          }
        });
      }
    }

    static get observedAttributes() {
      return ['selected-index'];
    }

    get selectedIndex() {
      const index = parseInt(this.getAttribute('selected-index'), 10);
      return isNaN(index) ? 0 : index;
    }

    set selectedIndex(index) {
      const clamped = Math.min(Math.max(index, 0), this.buttons.length - 1);
      this.setAttribute('selected-index', clamped.toString());
    }

    get buttons() {
      if (!this._buttons) {
        this._buttons = Array.from(this.hasAttribute('selector') ? this.querySelectorAll(this.getAttribute('selector')) : this.children);
      }
      return this._buttons;
    }

    get controlledElement() {
      if (!this.hasAttribute('aria-controls')) {
        return null;
      }
      return document.getElementById(this.getAttribute('aria-controls'));
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name !== 'selected-index' || oldValue === newValue) {
        return;
      }

      const selected = parseInt(newValue, 10);
      this.buttons.forEach((button, index) => {
        button.classList.toggle('active', index === selected);
        const mapButton = button.querySelector('[data-map-button]');
        if (mapButton) {
          mapButton.classList.toggle('button--secondary', index !== selected);
        }
      });

      const mapElement = this.controlledElement;
      if (!mapElement) {
        return;
      }

      const button = this.buttons[selected];
      if (!button) {
        return;
      }

      const address = button.getAttribute('data-map-address') || '';
      if (address) {
        mapElement.setAttribute('data-map-address', address);
      } else {
        mapElement.removeAttribute('data-map-address');
      }

      const latitude = button.getAttribute('data-latitude');
      const longitude = button.getAttribute('data-longitude');

      if (latitude && longitude) {
        mapElement.setAttribute('data-latitude', latitude);
        mapElement.setAttribute('data-longitude', longitude);
      } else {
        mapElement.removeAttribute('data-latitude');
        mapElement.removeAttribute('data-longitude');
      }

      if (typeof mapElement.prepMapApi === 'function') {
        mapElement.prepMapApi();
      }

      mapElement.initMap();
    }
  }

  if (!customElements.get('g-map')) {
    customElements.define('g-map', GMap);
  }

  if (!customElements.get('g-map-locations')) {
    customElements.define('g-map-locations', GMapLocations, { extends: 'ul' });
  }
})();
